import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Globals } from 'app/_globals';
import { NotificationService } from 'app/_services/notification.service';
import { SiteAssetsService } from 'app/_services/site-assets.service';
import { WorkflowItemService } from 'app/_services/workflow-items.service';
import { RestrictionsType } from 'app/_models/restrictions/restrictions-type';
import { hasWorkflowBackParams, readWorkflowBackParams } from 'app/_utils/workflow-back-link.utils';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'asset-restriction-approval',
  templateUrl: './asset-restriction-approval.component.html',
})
export class AssetRestrictionApprovalComponent implements OnInit {
  private siteId: any;
  private assetId: any;
  private workflowItemId: any;
  private routeRole: any;
  private backLinkParams: any = {};

  public saveInProgress = false;
  public tabitems: any[] = [];
  public isViewOnly = false;
  public pageTitle = 'Asset Restriction Pending Approval';

  public asset: any;
  public workflowItem: any;
  public showRestrictionForm = false;
  public showRejectionForm = false;
  public rejectionForm = '';
  public approveRestrictionForm: FormGroup;
  public restrictionTypes: { label: string; value: RestrictionsType  }[] = [
    { label: 'Operational', value: RestrictionsType.Operational },
    { label: 'Health & Safety', value: RestrictionsType.HealthSafety },
    { label: 'General', value: RestrictionsType.General },
  ];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private assetService: SiteAssetsService,
    private workflowItemService: WorkflowItemService,
    private notificationService: NotificationService,
    globals: Globals,
    fb: FormBuilder,
  ) {
    this.routeRole = globals.user.routeRole;

    this.approveRestrictionForm = fb.group({
      description: new FormControl('', Validators.required),
      type: new FormControl('', Validators.required),
      lockAsset: new FormControl(),
    });

  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.siteId = params.id;
      this.assetId = params.assetId;
      this.workflowItemId = params.workflowId;

      this.setupPage();
    });
  }

  private setupPage() {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.backLinkParams = readWorkflowBackParams(queryParams);

    const backLinkTabItem: any = {
      path: '/' + this.routeRole + '/pending-items',
      title: 'Inbox',
      class: 'back-link',
    };

    if (hasWorkflowBackParams(this.backLinkParams)) {
      backLinkTabItem.queryParams = this.backLinkParams;
    }

    this.tabitems = [backLinkTabItem];

    let getRequests = [];
    getRequests.push(this.assetService.getAssetById(this.assetId));
    getRequests.push(this.workflowItemService.getWorkflowItemById(this.workflowItemId));

    forkJoin(getRequests).subscribe(([asset, workflowItem]) => {
      this.asset = asset;
      this.workflowItem = workflowItem;

      if (!this.workflowItem) {
        this.notificationService.notify('warning', `Cannot find this approval item`, 'Likely it has already been approved or rejected.');
        this.navigateToInbox();
        return;
      }

      this.isViewOnly = this.isClosedWorkflowItem(this.workflowItem.status);
      this.pageTitle = this.isViewOnly ? 'Asset Restriction Details' : 'Asset Restriction Pending Approval';

      this.approveRestrictionForm.patchValue({
        description: this.workflowItem.restrictions,
        type: this.restrictionTypes.find((item) => item.value === this.workflowItem.restrictionType),
        lockAsset: this.workflowItem.lockAsset,
      });
      if (this.asset.locked) {
        this.approveRestrictionForm.get('lockAsset').disable();
      }
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', 'Error fetching the data for this page', 'Please try again later');
    });
  }

  public save() {
    if (this.isViewOnly) {
      return;
    }

    this.saveInProgress = true;

    let body = {
      status: 'APPROVED',
      updatedRestriction: this.approveRestrictionForm.value.description,
      restrictionType: this.approveRestrictionForm.value.type.value,
      lockAsset: this.approveRestrictionForm.value.lockAsset,
    };

    this.workflowItemService.approveOrRejectItem(this.workflowItemId, body).subscribe(() => {
      this.saveInProgress = false;
      this.notificationService.notify('success', 'Success', 'Restrictions saved');
      this.navigateToInbox();
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', `Couldn't update restrictions`, `Try again later`);
      this.saveInProgress = false;
    });
  }

  public approve() {
    if (this.isViewOnly) {
      return;
    }

    this.showRestrictionForm = true;
  }

  public cancelApprovalForm() {
    if (this.isViewOnly) {
      return;
    }

    this.showRestrictionForm = false;
    this.saveInProgress = false;
  }

  // Rejecting asks for a comment first.
  public reject() {
    if (this.isViewOnly) {
      return;
    }

    this.showRejectionForm = true;
  }

  public cancelRejection() {
    if (this.isViewOnly) {
      return;
    }

    this.showRejectionForm = false;
    this.rejectionForm = '';
    this.saveInProgress = false;
  }

  public saveRejection() {
    if (this.isViewOnly || this.saveInProgress || !this.rejectionForm.trim()) {
      return;
    }

    this.saveInProgress = true;

    let body = {
      status: 'REJECTED',
      comments: this.rejectionForm.trim(),
    };

    this.workflowItemService.approveOrRejectItem(this.workflowItemId, body).subscribe(() => {
      this.saveInProgress = false;
      this.notificationService.notify('success', 'Success', 'Request rejected');
      this.navigateToInbox();
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', `Couldn't save rejection`, `Try again later`);
      this.saveInProgress = false;
    });
  }

  private navigateToInbox() {
    const navigationExtras: any = {};

    if (hasWorkflowBackParams(this.backLinkParams)) {
      navigationExtras.queryParams = this.backLinkParams;
    }

    this.router.navigate(['/' + this.routeRole + '/pending-items'], navigationExtras);
  }

  private isClosedWorkflowItem(status: string): boolean {
    return ['APPROVED', 'REJECTED', 'DOCUMENT_REJECTED'].indexOf(status) !== -1;
  }
}
